/**
 * WaterfallPanel · Sprint 4 · v2.9.0
 *
 * Editor visual del Capital Structure multi-tranche:
 *  - Sponsor (capital, mes de aporte, shares tier 1/2)
 *  - Partners[]: LP1, LP2, LPN con capital, pref rate, calc, shares tier 1/2, mes
 *  - Tier 2 config: hurdle metric (MIRR / IRR), hurdle value, catch-up
 *
 * Persiste vía `waterfallAPI.save`.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Save, Users, Shield } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { waterfallAPI } from '../../lib/api';

function emptyPartner(idx) {
  return {
    id: `lp${idx + 1}`,
    name: `LP${idx + 1}`,
    capital: 0,
    capital_call_month: 1,
    pref_return_rate_annual: 0.08,
    pref_calculation: 'compound',
    residual_share_tier1: 0.2,
    residual_share_tier2: 0.3,
  };
}

const DEFAULT_STRUCTURE = {
  sponsor: {
    name: 'Sponsor',
    capital: 0,
    capital_call_month: 1,
    residual_share_tier1: 0.8,
    residual_share_tier2: 0.7,
  },
  partners: [],
  tier2: {
    enabled: false,
    hurdle_metric: 'mirr',
    hurdle_value: 0.18,
    catch_up_enabled: false,
    catch_up_target: 0.25,
  },
};

function fmtPct(v) {
  const n = Number(v) || 0;
  return `${(n * 100).toFixed(2)}%`;
}

function normalize(doc) {
  if (!doc) return { ...DEFAULT_STRUCTURE };
  const sponsor = { ...DEFAULT_STRUCTURE.sponsor, ...(doc.sponsor || {}) };
  // Adapta desde el legacy `investors`
  const rawPartners = doc.partners || doc.investors || [];
  const partners = rawPartners.map((p, i) => {
    const legacyPref = p.rate_or_pref_return || {};
    return {
      id: p.id || `lp${i + 1}`,
      name: p.name || `LP${i + 1}`,
      capital: Number(p.capital ?? p.capital_committed ?? 0),
      capital_call_month: Number(p.capital_call_month || 1),
      pref_return_rate_annual: Number(p.pref_return_rate_annual ?? legacyPref.value ?? 0),
      pref_calculation: p.pref_calculation || legacyPref.calculation || 'compound',
      residual_share_tier1: Number(p.residual_share_tier1 ?? p.share_t1 ?? 0),
      residual_share_tier2: Number(p.residual_share_tier2 ?? p.share_t2 ?? p.residual_share_tier1 ?? 0),
    };
  });
  const tier2 = { ...DEFAULT_STRUCTURE.tier2, ...(doc.tier2 || {}) };
  return { sponsor, partners, tier2 };
}

export function WaterfallPanel({ scenarioId }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['capital-structure', scenarioId],
    queryFn: async () => (await waterfallAPI.get(scenarioId)).data,
    enabled: !!scenarioId,
    staleTime: 60000,
  });
  const [struct, setStruct] = useState(DEFAULT_STRUCTURE);

  useEffect(() => {
    if (data) setStruct(normalize(data));
  }, [data]);

  const saveMut = useMutation({
    mutationFn: (payload) => waterfallAPI.save(scenarioId, payload),
    onSuccess: () => {
      toast.success('Estructura de capital guardada');
      qc.invalidateQueries({ queryKey: ['capital-structure', scenarioId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Error al guardar'),
  });

  const totalT1 = useMemo(() => {
    return (struct.sponsor?.residual_share_tier1 || 0) + struct.partners.reduce((s, p) => s + (p.residual_share_tier1 || 0), 0);
  }, [struct]);
  const totalT2 = useMemo(() => {
    return (struct.sponsor?.residual_share_tier2 || 0) + struct.partners.reduce((s, p) => s + (p.residual_share_tier2 || 0), 0);
  }, [struct]);
  const totalCapital = useMemo(() => {
    return (struct.sponsor?.capital || 0) + struct.partners.reduce((s, p) => s + (p.capital || 0), 0);
  }, [struct]);

  const updSponsor = (k, v) => setStruct({ ...struct, sponsor: { ...struct.sponsor, [k]: v } });
  const updPartner = (idx, k, v) => {
    const next = [...struct.partners];
    next[idx] = { ...next[idx], [k]: v };
    setStruct({ ...struct, partners: next });
  };
  const addPartner = () => setStruct({ ...struct, partners: [...struct.partners, emptyPartner(struct.partners.length)] });
  const removePartner = (idx) => setStruct({ ...struct, partners: struct.partners.filter((_, i) => i !== idx) });
  const updTier2 = (k, v) => setStruct({ ...struct, tier2: { ...struct.tier2, [k]: v } });

  const save = () => {
    // Persist en shape v2 (partners) + legacy investors para compatibilidad
    saveMut.mutate({
      sponsor: struct.sponsor,
      partners: struct.partners,
      investors: struct.partners.map(p => ({
        id: p.id, name: p.name,
        capital_committed: p.capital,
        capital_calls: [{ month: p.capital_call_month, amount: p.capital }],
        rate_or_pref_return: { value: p.pref_return_rate_annual, calculation: p.pref_calculation },
      })),
      tier2: struct.tier2,
      waterfall: [], // legacy campo, no usado por partner_waterfall
    });
  };

  if (isLoading) return <div className="text-xs text-muted-foreground">Cargando estructura de capital…</div>;

  return (
    <div className="rounded-2xl border border-border/40 bg-card p-6 space-y-4" data-testid="waterfall-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold" style={{ color: '#1E293B' }}>
            <Users className="inline w-4 h-4 mr-1" style={{ color: '#4F46E5' }} />
            Waterfall multi-tranche (Sprint 4 · v2.9.0)
          </h4>
          <p className="text-[11px] mt-1" style={{ color: '#64748B' }}>
            Configura Sponsor + Partners con capital, preferentes y splits individuales. Soporta Tier 2 promote escalonado (arriba del hurdle).
          </p>
        </div>
        <Button size="sm" onClick={save} disabled={saveMut.isPending} data-testid="save-waterfall" style={{ background: '#4F46E5', color: 'white' }}>
          <Save className="w-3.5 h-3.5 mr-1" />{saveMut.isPending ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>

      {/* Sponsor */}
      <div className="rounded-xl border p-4" style={{ borderColor: 'rgba(79,70,229,0.35)', background: 'rgba(79,70,229,0.03)' }} data-testid="sponsor-row">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4" style={{ color: '#4F46E5' }} />
          <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#4F46E5' }}>Sponsor / Desarrollador</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <FieldNum label="Nombre" isText value={struct.sponsor.name} onChange={v => updSponsor('name', v)} testId="sponsor-name" />
          <FieldNum label="Capital ($)" value={struct.sponsor.capital} onChange={v => updSponsor('capital', v)} step={100000} testId="sponsor-capital" />
          <FieldNum label="Mes aporte" value={struct.sponsor.capital_call_month} onChange={v => updSponsor('capital_call_month', v)} step={1} min={1} testId="sponsor-month" />
          <FieldNum label="Share T1 (0-1)" value={struct.sponsor.residual_share_tier1} onChange={v => updSponsor('residual_share_tier1', v)} step={0.05} min={0} max={1} testId="sponsor-t1" />
          <FieldNum label="Share T2 (0-1)" value={struct.sponsor.residual_share_tier2} onChange={v => updSponsor('residual_share_tier2', v)} step={0.05} min={0} max={1} testId="sponsor-t2" />
        </div>
      </div>

      {/* Partners */}
      <div className="rounded-xl border p-4" style={{ borderColor: 'rgba(3,105,161,0.35)', background: 'rgba(3,105,161,0.03)' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#0369A1' }}>Socios inversionistas (LPs)</div>
          <Button size="sm" variant="ghost" onClick={addPartner} data-testid="add-partner" className="h-7 text-[11px]">
            <Plus className="w-3 h-3 mr-1" />Agregar LP
          </Button>
        </div>
        {struct.partners.length === 0 ? (
          <div className="text-[11px] text-center py-4" style={{ color: '#64748B' }}>Sin socios capturados. Agrega LP1, LP2, etc.</div>
        ) : (
          <div className="space-y-3">
            {struct.partners.map((p, idx) => (
              <div key={idx} className="rounded-lg border p-3" style={{ borderColor: 'rgba(3,105,161,0.25)', background: 'white' }} data-testid={`partner-row-${idx}`}>
                <div className="grid grid-cols-2 md:grid-cols-8 gap-2">
                  <FieldNum label="Nombre" isText value={p.name} onChange={v => updPartner(idx, 'name', v)} testId={`partner-name-${idx}`} />
                  <FieldNum label="Capital ($)" value={p.capital} onChange={v => updPartner(idx, 'capital', v)} step={100000} testId={`partner-capital-${idx}`} />
                  <FieldNum label="Mes aporte" value={p.capital_call_month} onChange={v => updPartner(idx, 'capital_call_month', v)} step={1} min={1} testId={`partner-month-${idx}`} />
                  <FieldNum label="Preferente" value={p.pref_return_rate_annual} onChange={v => updPartner(idx, 'pref_return_rate_annual', v)} step={0.01} min={0} testId={`partner-pref-${idx}`} suffix={fmtPct(p.pref_return_rate_annual)} />
                  <div>
                    <label className="text-[10px] font-semibold" style={{ color: '#64748B' }}>Cálculo pref</label>
                    <Select value={p.pref_calculation} onValueChange={v => updPartner(idx, 'pref_calculation', v)}>
                      <SelectTrigger className="h-8 text-[11px]" data-testid={`partner-calc-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="compound">Compuesto anual</SelectItem>
                        <SelectItem value="compound_monthly">Compuesto mensual</SelectItem>
                        <SelectItem value="simple">Simple</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <FieldNum label="Share T1" value={p.residual_share_tier1} onChange={v => updPartner(idx, 'residual_share_tier1', v)} step={0.05} min={0} max={1} testId={`partner-t1-${idx}`} />
                  <FieldNum label="Share T2" value={p.residual_share_tier2} onChange={v => updPartner(idx, 'residual_share_tier2', v)} step={0.05} min={0} max={1} testId={`partner-t2-${idx}`} />
                  <div className="flex items-end justify-end">
                    <Button size="sm" variant="ghost" onClick={() => removePartner(idx)} className="h-8 w-8 p-0" data-testid={`partner-del-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tier 2 config */}
      <div className="rounded-xl border p-4" style={{ borderColor: 'rgba(124,58,237,0.35)', background: 'rgba(124,58,237,0.03)' }}>
        <div className="flex items-center gap-2 mb-3">
          <input
            type="checkbox"
            checked={!!struct.tier2.enabled}
            onChange={e => updTier2('enabled', e.target.checked)}
            data-testid="tier2-enabled"
          />
          <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#7C3AED' }}>
            Tier 2 promote escalonado (arriba del hurdle)
          </label>
        </div>
        {struct.tier2.enabled && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] font-semibold" style={{ color: '#64748B' }}>Métrica hurdle</label>
              <Select value={struct.tier2.hurdle_metric} onValueChange={v => updTier2('hurdle_metric', v)}>
                <SelectTrigger className="h-8 text-[11px]" data-testid="tier2-metric"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mirr">MIRR</SelectItem>
                  <SelectItem value="irr">IRR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FieldNum label="Hurdle" value={struct.tier2.hurdle_value} onChange={v => updTier2('hurdle_value', v)} step={0.01} min={0} testId="tier2-hurdle" suffix={fmtPct(struct.tier2.hurdle_value)} />
            <div className="flex items-end gap-2">
              <input type="checkbox" checked={!!struct.tier2.catch_up_enabled} onChange={e => updTier2('catch_up_enabled', e.target.checked)} data-testid="tier2-catchup-enabled" />
              <label className="text-[11px]" style={{ color: '#334155' }}>Catch-up habilitado</label>
            </div>
            {struct.tier2.catch_up_enabled && (
              <FieldNum label="Target promote" value={struct.tier2.catch_up_target} onChange={v => updTier2('catch_up_target', v)} step={0.05} min={0} max={1} testId="tier2-catchup" suffix={fmtPct(struct.tier2.catch_up_target)} />
            )}
          </div>
        )}
      </div>

      {/* Totales de validación */}
      <div className="rounded-xl border-2 border-dashed p-3 text-[11px] flex gap-6 flex-wrap" style={{ borderColor: '#94A3B8', color: '#334155' }} data-testid="waterfall-totals">
        <span>Capital total: <strong>${totalCapital.toLocaleString('en-US')}</strong></span>
        <span>Σ Share T1: <strong style={{ color: Math.abs(totalT1 - 1) < 0.001 ? '#16A34A' : '#DC2626' }}>{(totalT1 * 100).toFixed(1)}%</strong> (debe = 100%)</span>
        <span>Σ Share T2: <strong style={{ color: Math.abs(totalT2 - 1) < 0.001 ? '#16A34A' : '#DC2626' }}>{(totalT2 * 100).toFixed(1)}%</strong> (debe = 100%)</span>
      </div>
    </div>
  );
}

function FieldNum({ label, value, onChange, step = 1, min, max, testId, isText, suffix }) {
  return (
    <div>
      <label className="text-[10px] font-semibold" style={{ color: '#64748B' }}>{label}</label>
      <Input
        type={isText ? 'text' : 'number'}
        step={isText ? undefined : step}
        min={isText ? undefined : min}
        max={isText ? undefined : max}
        value={value ?? ''}
        onChange={(e) => onChange(isText ? e.target.value : (parseFloat(e.target.value) || 0))}
        className="h-8 text-[11px]"
        data-testid={testId}
      />
      {suffix && <div className="text-[9px] mt-0.5" style={{ color: '#94A3B8' }}>{suffix}</div>}
    </div>
  );
}
